
import { useState, useRef, useEffect } from 'react';

interface ShareEventButtonProps {
  event: {
    id: number;
    title: string;
    month: string;
    day: string;
    location: string;
    time: string;
  };
  variant?: 'icon' | 'compact' | 'primary';
}

export default function ShareEventButton({ event, variant = 'compact' }: ShareEventButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const eventUrl = `${window.location.origin}${(window as any).__BASE_PATH__ || ''}/events/${event.id}`;
  const shareText = `${event.title} — ${event.month} ${event.day} at ${event.location}. ${event.time}`;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const shareOptions = [
    {
      name: 'Facebook',
      icon: 'ri-facebook-fill',
      color: 'text-[#1877F2]',
      bg: 'bg-[#1877F2]/10',
      action: () => {
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(eventUrl)}&quote=${encodeURIComponent(shareText)}`,
          '_blank',
          'width=600,height=400'
        );
      },
    },
    {
      name: 'X (Twitter)',
      icon: 'ri-twitter-x-fill',
      color: 'text-gray-900',
      bg: 'bg-gray-900/10',
      action: () => {
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(eventUrl)}`,
          '_blank',
          'width=600,height=400'
        );
      },
    },
    {
      name: 'LinkedIn',
      icon: 'ri-linkedin-fill',
      color: 'text-[#0A66C2]',
      bg: 'bg-[#0A66C2]/10',
      action: () => {
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(eventUrl)}`,
          '_blank',
          'width=600,height=400'
        );
      },
    },
    {
      name: 'WhatsApp',
      icon: 'ri-whatsapp-fill',
      color: 'text-[#25D366]',
      bg: 'bg-[#25D366]/10',
      action: () => {
        window.open(
          `https://wa.me/?text=${encodeURIComponent(shareText + '\n' + eventUrl)}`,
          '_blank'
        );
      },
    },
    {
      name: 'Email',
      icon: 'ri-mail-fill',
      color: 'text-amber-600',
      bg: 'bg-amber-600/10',
      action: () => {
        window.open(
          `mailto:?subject=${encodeURIComponent(event.title + ' — Roseville Event')}&body=${encodeURIComponent(shareText + '\n\nMore details: ' + eventUrl)}`,
          '_self'
        );
      },
    },
    {
      name: copied ? 'Copied!' : 'Copy Link',
      icon: copied ? 'ri-check-line' : 'ri-link',
      color: copied ? 'text-teal-600' : 'text-gray-600',
      bg: copied ? 'bg-teal-600/10' : 'bg-gray-600/10',
      action: () => {
        navigator.clipboard.writeText(eventUrl).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        });
      },
    },
  ];

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (e: React.MouseEvent, action: () => void) => {
    e.preventDefault();
    e.stopPropagation();
    action();
  };

  if (variant === 'icon') {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={handleToggle}
          className="w-10 h-10 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors cursor-pointer"
          aria-label="Share event"
        >
          <i className="ri-share-forward-fill text-teal-600 text-lg"></i>
        </button>
        {isOpen && (
          <div className="absolute right-0 top-12 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 w-52 z-50 animate-fade-in">
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Share Event</p>
            </div>
            {shareOptions.map((option) => (
              <button
                key={option.name}
                onClick={(e) => handleOptionClick(e, option.action)}
                className="w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${option.bg}`}>
                  <i className={`${option.icon} ${option.color} text-base`}></i>
                </div>
                <span className="text-sm font-medium text-gray-700">{option.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={handleToggle}
          className="flex items-center space-x-1.5 text-gray-500 hover:text-teal-600 transition-colors cursor-pointer text-sm"
          aria-label="Share event"
        >
          <i className="ri-share-forward-line text-base"></i>
          <span className="font-medium">Share</span>
        </button>
        {isOpen && (
          <div className="absolute right-0 top-8 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 w-52 z-50 animate-fade-in">
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Share Event</p>
            </div>
            {shareOptions.map((option) => (
              <button
                key={option.name}
                onClick={(e) => handleOptionClick(e, option.action)}
                className="w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${option.bg}`}>
                  <i className={`${option.icon} ${option.color} text-base`}></i>
                </div>
                <span className="text-sm font-medium text-gray-700">{option.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // primary variant
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggle}
        className="w-full flex items-center justify-center space-x-2 bg-amber-50 text-amber-700 py-3 rounded-lg font-semibold hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap"
        aria-label="Share event"
      >
        <i className="ri-share-forward-line text-lg"></i>
        <span>Share Event</span>
      </button>
      {isOpen && (
        <div className="absolute left-0 right-0 bottom-14 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50 animate-fade-in">
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Share via</p>
          </div>
          <div className="grid grid-cols-3 gap-1 p-3">
            {shareOptions.map((option) => (
              <button
                key={option.name}
                onClick={(e) => handleOptionClick(e, option.action)}
                className="flex flex-col items-center space-y-1.5 p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <div className={`w-10 h-10 flex items-center justify-center rounded-full ${option.bg}`}>
                  <i className={`${option.icon} ${option.color} text-lg`}></i>
                </div>
                <span className="text-xs font-medium text-gray-600 whitespace-nowrap">{option.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
